import React, { useRef, useState } from "react";
import { styles } from "./HotSpot.style";
import { HotSpotType } from "./HotSpotType";
import { useClickAway } from "../../../hooks";
import { Icon, Icons } from "../../../elements/Icon";

interface HotSpotProps {
  hotSpot: HotSpotType;
}

export const HotSpot = (props: HotSpotProps) => {
  const { hotSpot } = props;

  if (!hotSpot) {
    return null;
  }

  const {
    id,
    hotSpotAriaLabel,
    hotSpotDesktopX,
    hotSpotDesktopY,
    hotSpotMobileX,
    hotSpotMobileY,
    tooltipTitle,
    tooltipDescription,
  } = hotSpot;

  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Tab") {
      setShowTooltip(false);
    }
  };

  useClickAway(tooltipRef, () => setShowTooltip(false), !showTooltip);

  const descriptionWithLineBreaks =
    tooltipDescription &&
    tooltipDescription
      .split("\n")
      .map((line, index) => {
        const text = line.trim();
        if (!text) {
          return null;
        }
        return <p key={index}>{text}</p>;
      })
      .filter(Boolean);

  const tooltipOnTop = hotSpotDesktopY > 50;
  const tooltipToLeft = hotSpotDesktopX > 50;

  const hasTooltip = !!tooltipTitle || !!tooltipDescription;

  return (
    <>
      <div
        className={styles.hotSpotWrapper(showTooltip)}
        style={
          {
            "--hotspot-desktop-x": hotSpotDesktopX ? `${hotSpotDesktopX}%` : "0%",
            "--hotspot-desktop-y": hotSpotDesktopY ? `${hotSpotDesktopY}%` : "0%",
            "--hotspot-mobile-x": hotSpotMobileX ? `${hotSpotMobileX}%` : "0%",
            "--hotspot-mobile-y": hotSpotMobileY ? `${hotSpotMobileY}%` : "0%",
          } as React.CSSProperties
        }
        ref={tooltipRef}
      >
        <button
          aria-label={hotSpotAriaLabel}
          aria-describedby={tooltipDescription ? id : undefined}
          className={styles.hotSpot()}
          onClick={() => setShowTooltip(!showTooltip)}
          onKeyDown={(event) => handleKeyDown(event)}
          disabled={!hasTooltip}
        />
        {hasTooltip && (
          <div className={styles.tooltip(tooltipOnTop, tooltipToLeft, showTooltip)}>
            {tooltipTitle && <p className={styles.tooltipTitle()}>{tooltipTitle}</p>}
            {descriptionWithLineBreaks && (
              <div id={id} className={styles.tooltipDescription()}>
                {descriptionWithLineBreaks}
              </div>
            )}
            <button className={styles.tooltipCloseButton()} onClick={() => setShowTooltip(false)}>
              <Icon icon={Icons.close} />
            </button>
          </div>
        )}
      </div>
      {showTooltip && <div className={styles.tooltipBackdrop()} />}
    </>
  );
};
